<template>
  <li
    class="step-card"
    data-ui="step-card"
    data-analytics-config="exclude-all"
  >
    <div
      class="step-card__inner"
      :class="cardClass"
    >
      <div
        v-if="image"
        class="step-card__image"
      >
        <cdr-img
          :src="image?.src"
          v-bind="conditionalLazyLoad"
          :alt="image?.alt"
          :aria-hidden="!image?.alt"
        />
      </div>
      <div>
        <a
          v-if="target"
          :href="target"
          class="step-card__link"
          @click="triggerAnalytics"
        >
          <h3
            class="step-card__title"
            v-html="heading"
          />
        </a>
        <h3
          v-if="!target"
          class="step-card__title"
          v-html="heading"
        />
        <p
          class="step-card__text"
          v-html="body"
        />
        <div
          v-if="isNew"
          class="new-tag"
        >
          <span>new!</span>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { CdrImg } from '@rei/cedar';
import { metricsLink } from '../../../utils/analytics';

export default {
  name: 'StepCard',
  components: {
    CdrImg,
  },
  props: {
    body: {
      type: String,
      required: true,
    },
    heading: {
      type: String,
      required: true,
    },
    isNew: {
      type: Boolean,
      required: false,
      default: false,
    },
    lazyLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
    image: {
      type: Object,
      required: true,
    },
    target: {
      type: String,
      required: false,
      default: '',
    },
    index: {
      type: Number,
      required: true,
    },
    memberMoment: {
      type: Boolean,
      required: false,
      default: false,
    },
    showDecor: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    cardClass() {
      return {
        'step-card__inner--interactive': this.target,
        'step-card__inner--flat-layout': this.memberMoment,
        'step-card__inner--decoration': this.showDecor,

      };
    },
    conditionalLazyLoad() {
      return this.lazyLoad ? { loading: 'lazy' } : {};
    },
  },
  methods: {
    /* c8 ignore start */
    triggerAnalytics() {
      metricsLink(`A lifetime of benefits_${this.index}_${this.heading}`);
    },
    /* c8 ignore end */
  },
};
</script>

<style lang="scss">

.step-card {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  &__image {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    position: absolute;
    bottom: 70%;
    right: 0;
    left: 0;
    width: 128px;
    height: 128px;
  }

  img {
    height: 110px;
  }

  &__inner {
    height: 300px;
    background: $cdr-color-background-primary;
    border-radius: 10px;
    padding-top: 100px;
    margin-top: 4.4rem;
    padding: 95px $cdr-space-one-x $cdr-space-one-and-a-half-x;
    position: relative;
    transition: .25s ;
    transition-timing-function: ease-in-out;
  }

  & &__inner.step-card__inner--flat-layout {
    display: grid;
    grid-template-columns: 1fr 3fr;
    grid-gap: $cdr-space-half-x;
    padding-top: $cdr-space-two-x;
    margin-top: 0;
    margin-bottom: $cdr-space-one-x;
    flex: 1;

    .step-card__image {
      display: block;
      position: initial;
      width: 100% ;
    }
    img {
      height: 55px;
      display: block;
      margin: 0 auto;
    }
    .step-card__text,
    .step-card__title {
      text-align: initial;
      display: block;
      height: auto;
      margin-top: 0;
    }
    .step-card__text {
      @include cdr-text-body-300;
      color: $cdr-color-text-primary;
    }
    .step-card__title {
      @include cdr-text-heading-sans-400;
    }
    @include cdr-md-mq-up {
      margin-bottom: $cdr-space-one-and-a-half-x;
    }
    a {
      color: $cdr-color-text-link-rest;
      cursor: pointer;
    }
  }
  $sparkle1:'/assets/membership/member-moment/sparkle1/sparkle1.svg';
  $sparkle2:'/assets/membership/member-moment/sparkle2/sparkle2.svg';
  .step-card__inner--decoration{
    background: url($sparkle1) left bottom, url($sparkle2) right -10px top -10px;
    background-repeat: no-repeat;
    background-origin: content-box;
    background-size: 40px;

    @include cdr-md-mq-up {
      background: url($sparkle1) left bottom, url($sparkle2) right top -6px;
      background-repeat: no-repeat;
      background-origin: content-box;
      background-size: 40px;
    }
  }

  &__title {
    @include cdr-text-heading-sans-400;
    height: 4.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.1rem;
    color: $cdr-color-text-brand;
    text-align: center;
    margin: 0 0 $cdr-space-three-quarter-x;
  }

  &__text {
    @include cdr-text-heading-sans-200;
    line-height: 2.2rem;
    font-weight: 400;
    text-align: center;
    color: $cdr-color-text-emphasis;

    a:hover {
      text-decoration: none;
    }
  }

  &__link {
    text-decoration: none;
  }

  &__link::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    display: block;
    z-index: 1;
  }

  &__link:hover{
    color: $cdr-color-text-brand;
  }
}

.step-card__inner--interactive:active,
.step-card__inner--interactive:focus,
.step-card__inner--interactive:hover {
    box-shadow: $cdr-prominence-floating;
    transform: translateY(-0.2rem);
}
.new-tag {
  @include cdr-text-eyebrow-100;
   font-weight: 600;
   display: flex;
   align-items: center;
   justify-content: center;
   width: 52px;
   height: 22px;
   margin: $cdr-space-one-x auto 0;
   background-color: $cdr-color-background-brand-spruce;
   border-radius: 16px;
   color: $cdr-color-text-button-primary;
}

</style>
